import cn from '../utils/cn';

// The logo pack's loader (svg/animated/koob-loader-fill-*.svg): over a pale
// B, the top half fills from the left, the bottom half a beat later, both
// fade, and it starts over. The one spinner of both apps (ESLint bans the
// others). Its own keyframes, as the apps' Tailwind configs differ; under
// prefers-reduced-motion it holds the filled B.
export const LOADER_STYLE = `
.koob-loader-fill{transform-box:fill-box;transform-origin:left center;animation:koob-loader-top 1.8s cubic-bezier(.6,0,.2,1) infinite}
.koob-loader-late{animation-name:koob-loader-bottom}
@keyframes koob-loader-top{0%{transform:scaleX(0);opacity:1}35%,80%{transform:scaleX(1);opacity:1}100%{transform:scaleX(1);opacity:0}}
@keyframes koob-loader-bottom{0%,20%{transform:scaleX(0);opacity:1}55%,80%{transform:scaleX(1);opacity:1}100%{transform:scaleX(1);opacity:0}}
@media (prefers-reduced-motion:reduce){.koob-loader-fill{animation:none}}
.koob-loader-delayed{animation:koob-loader-in 0s .3s both}
@keyframes koob-loader-in{from{opacity:0}}
`;

const TOP = 'M28 22H54A13 13 0 0 1 54 48H28Z';
const BOTTOM = 'M28 52H60A13 13 0 0 1 60 78H28Z';

type KoobLoaderProps = {
  /** Height in px. */
  size?: number;
  /** One colour instead of navy and orange: `currentColor` in buttons. */
  color?: string;
  /** In a centred block at least 120px tall, for a pane that loads. Shows
   * after 300ms, so a fast load never flashes it. */
  centered?: boolean;
  /** On navy: a white top half over a dark track (the pack's dark loader). */
  onDark?: boolean;
  /** Grays only, a light track and a darker fill: inline in a field, where
   * the brand colours would draw the eye (KoobInputSelect). */
  muted?: boolean;
  className?: string;
  /** What loads, for screen readers (the mark itself is hidden from them). */
  label?: string;
};

/** Koob's loading mark: for a pane whose shape is unknown, or inline. Content
 * whose shape is known loads with KoobSkeleton, an action with KoobButton's
 * `loading`, a long blocking job with KoobLoadingOverlay. */
export default function KoobLoader({
  size = 48,
  color,
  centered = false,
  onDark = false,
  muted = false,
  className,
  label,
}: KoobLoaderProps) {
  // Mono: the track is the same colour, faint (the pack's currentcolor loader).
  // Muted: gray-200 under gray-400.
  const track = color
    ? { fill: color, fillOpacity: 0.18 }
    : { fill: muted ? '#E5E7EB' : onDark ? '#2A3B5C' : '#E3E7EE' };
  const fillClass = muted
    ? 'fill-gray-400'
    : onDark
      ? 'fill-white'
      : 'fill-k-h';
  const svg = (
    <svg
      className={cn('koob-loader inline-block shrink-0', className)}
      viewBox="24 18 53 64"
      height={size}
      width={(size * 53) / 64}
      aria-hidden
    >
      <style>{LOADER_STYLE}</style>
      <path d={TOP} {...track} />
      <path
        className={cn('koob-loader-fill', !color && fillClass)}
        fill={color}
        d={TOP}
      />
      <path d={BOTTOM} {...track} />
      <path
        className={cn(
          'koob-loader-fill koob-loader-late',
          !color && (muted ? 'fill-gray-400' : 'fill-k-orange'),
        )}
        fill={color}
        d={BOTTOM}
      />
    </svg>
  );
  const status = label && (
    <span role="status" className="sr-only">
      {label}
    </span>
  );
  if (!centered)
    return status ? (
      <span className="inline-flex">
        {svg}
        {status}
      </span>
    ) : (
      svg
    );
  return (
    <div className="koob-loader-delayed flex min-h-[120px] w-full items-center justify-center">
      {svg}
      {status}
    </div>
  );
}
